/**
 * Auth API — POST /auth/web.
 *
 * Sends a Google id_token (from GIS for Web) to the Worker. On success
 * the Worker sets a `session` httpOnly cookie scoped to
 * api.loaf.binodswain.dev. The response body carries the user + usage
 * summary so the SPA can hydrate immediately.
 */

import { apiDelete, apiPost } from '../api/client';

export interface LoafUser {
  sub: string;
  email: string;
  name: string;
  picture?: string | null;
  is_pro: boolean;
  tier: string;
  transcript_sync_enabled?: boolean;
}

export interface UsageSummary {
  stt_sec_used?: number;
  stt_sec_limit?: number;
  words?: number;
  [key: string]: unknown;
}

export interface AuthWebResponse {
  user: LoafUser;
  usage: UsageSummary;
}

/**
 * Exchange a Google id_token for a session cookie.
 *
 * The browser sets the `Origin: https://loaf.binodswain.dev` header
 * automatically; the Worker validates it alongside the id_token.
 */
export function signInWithGoogleIdToken(idToken: string): Promise<AuthWebResponse> {
  return apiPost<AuthWebResponse>('/auth/web', { id_token: idToken }, {
    skipAuthRedirect: true,
  });
}

/**
 * Sign out — clear the server-side session cookie.
 *
 * Calls `DELETE /auth/web` so the Worker invalidates the httpOnly
 * `session` cookie. The cookie is httpOnly so it cannot be cleared by
 * client-side JS; the server-side handler must exist for this to work.
 * TODO(worker): add the `DELETE /auth/web` handler that clears the cookie.
 * `bestEffort` swallows network/404 errors so the client still redirects
 * even if the endpoint is unavailable.
 */
export async function signOut(bestEffort = true): Promise<void> {
  try {
    await apiDelete<void>('/auth/web', { skipAuthRedirect: true });
  } catch (err) {
    if (!bestEffort) throw err;
    // Best-effort: the cookie may already be gone or the endpoint may
    // not exist yet. Either way, proceed to redirect to /auth.
  }
}
